import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, MessageCircle, Phone } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { IMAGES, CONTACT, SITE } from "../siteConfig";
import { Reveal } from "../components/Reveal";


const TECHNICAL_COLUMN_LABELS = {
  size: "Size",
  version: "Version",
  tra_code: "TRA Code",
  star_rating: "Star Rating",
  li_ss: "LI/SS",
  pr: "PR",
  rim_rec: "Rim (Rec)",
  rim_alt: "Rim (Alt)",
  ow: "OW",
  od: "OD",
  slr: "SLR",
  rc: "RC",
  tire_type: "Tyre Type",
  tread_depth: "Tread Depth",
  tkph: "TKPH",
  tmph: "TMPH",
};

const labelize = (key) => TECHNICAL_COLUMN_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setP(null);
    setMissing(false);
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="pt-[72px] min-h-screen" data-testid="product-missing">
        <Seo title="Product Not Found | Mangalam Group" noindex />
        <div className="mx-auto max-w-7xl px-4 py-32 text-center">
          <p className="font-condensed text-3xl font-bold uppercase text-white">Product not found</p>
          <Link to="/products" data-testid="product-missing-back" className="mt-4 inline-block font-mono text-[11px] tracking-[0.2em] text-ember uppercase">
            Back to catalogue
          </Link>
        </div>
      </div>
    );
  }
  if (!p) return <div className="min-h-screen bg-obsidian pt-[72px]" />;

  const summarySpecs = [
    ["Brand", p.brand],
    ["Tyre Size", p.size],
    ["Pattern", p.pattern],
    ["Construction", p.construction],
    ["Tyre Type", p.tyre_type],
    ["Category", p.category],
    ["Position", p.position],
    ["Applications", (p.applications || []).join(", ")],
    ["Equipment", (p.equipment || []).join(", ")],
  ].filter(([, v]) => v);
  const preferredSpecKeys = Object.keys(TECHNICAL_COLUMN_LABELS);
  const importedSpecKeys = [...new Set((p.specs || []).flatMap((row) => Object.keys(row)))];
  const technicalColumns = [
    ...preferredSpecKeys.filter((key) => importedSpecKeys.includes(key)),
    ...importedSpecKeys.filter((key) => !preferredSpecKeys.includes(key)),
  ];

  const waText = encodeURIComponent(`Enquiry: ${p.brand} ${p.name} — ${p.size}`);
  const productUrl = new URL(`/products/${p.id}`, SITE.url).toString();
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${p.brand} ${p.name}${p.size ? ` — ${p.size}` : ""}`,
    description: p.description,
    image: p.image,
    url: productUrl,
    brand: { "@type": "Brand", name: p.brand },
    category: p.category,
    additionalProperty: [
      ["Pattern", p.pattern],
      ["Construction", p.construction],
      ["Tyre Type", p.tyre_type],
      ["Applications", (p.applications || []).join(", ")],
      ["Equipment", (p.equipment || []).join(", ")],
    ].filter(([, value]) => value).map(([name, value]) => ({ "@type": "PropertyValue", name, value })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
      { "@type": "ListItem", position: 2, name: "Products", item: new URL("/products", SITE.url).toString() },
      { "@type": "ListItem", position: 3, name: p.name, item: productUrl },
    ],
  };

  return (
    <div data-testid="product-detail-page" className="pt-[72px]">
      <Seo
        title={`${p.brand} ${p.name} ${p.size} — Specifications | Mangalam Group`}
        description={p.description || `${p.brand} ${p.name} tyre in size ${p.size}. Available from Mangalam Group, Dhanbad, Jharkhand.`}
        path={`/products/${p.id}`}
        jsonLd={[productSchema, breadcrumbSchema]}
      />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            data-testid="product-back"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-400 transition-colors hover:text-ember"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to catalogue
          </Link>

          <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:items-start">
            <Reveal>
              <div className="relative overflow-hidden border border-white/10 bg-obsidian-2 tech-grid">
                <img
                  src={p.image || IMAGES.tread}
                  alt={`${p.brand} ${p.name} ${p.size} tyre`}
                  data-testid="product-detail-image"
                  className="h-[340px] sm:h-[460px] w-full object-contain p-8 img-industrial"
                />
                <span className="absolute left-4 top-4 bg-obsidian/85 backdrop-blur-sm px-2.5 py-1 font-mono text-[9px] tracking-[0.22em] uppercase text-white">
                  {p.brand}
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="font-mono text-xs tracking-[0.28em] text-ember">{p.size}</p>
              <h1 className="mt-3 font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.98] tracking-tight text-white">
                {p.name}
              </h1>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {[p.tyre_type, p.construction, p.position].filter(Boolean).map((t) => (
                  <span key={t} className="border border-white/10 px-2.5 py-1 font-mono text-[9px] tracking-[0.18em] uppercase text-slate-300">
                    {t}
                  </span>
                ))}
              </div>
              {p.description && (
                <p className="mt-6 text-base leading-relaxed text-slate-400">{p.description}</p>
              )}

              {(p.features || []).length > 0 && (
                <div className="mt-6" data-testid="product-features">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">Key Features</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {p.features.map((feature) => (
                      <span key={feature} className="border border-white/10 bg-obsidian-2 px-2.5 py-1 font-mono text-[10px] tracking-wide text-slate-300">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(p.sizes || []).length > 0 && (
                <div className="mt-6" data-testid="product-sizes">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">
                    Available Sizes ({p.sizes.length})
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {p.sizes.map((s) => (
                      <span key={s} className="border border-white/10 bg-obsidian-2 px-2.5 py-1 font-mono text-[10px] tracking-wide text-slate-300">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div data-testid="product-spec-table" className="mt-8 divide-y divide-white/5 border border-white/10">
                {summarySpecs.map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-6 bg-obsidian-2 px-5 py-3.5">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">{label}</span>
                    <span className="text-right text-sm font-medium text-white">{value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={`${CONTACT.whatsapp}?text=${waText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-whatsapp-cta"
                  className="inline-flex items-center gap-2 bg-ember px-6 py-4 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover"
                >
                  <MessageCircle className="h-4 w-4" /> ENQUIRE ON WHATSAPP
                </a>
                <a
                  href={CONTACT.phoneHref}
                  data-testid="product-call-cta"
                  className="inline-flex items-center gap-2 border border-white/20 px-6 py-4 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
                >
                  <Phone className="h-4 w-4" /> CALL US
                </a>
                <Link
                  to="/contact"
                  data-testid="product-enquiry-cta"
                  className="inline-flex items-center gap-2 border border-white/20 px-6 py-4 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
                >
                  SEND ENQUIRY <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <p className="mt-5 font-mono text-[10px] tracking-[0.12em] uppercase text-slate-300">
                Specifications confirmed on enquiry. Datasheets available on request.
              </p>
            </Reveal>
          </div>

          {(p.specs || []).length > 0 && (
            <div className="mt-14" data-testid="technical-specifications">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">Technical Data</p>
                  <h2 className="mt-3 font-condensed text-3xl font-bold uppercase tracking-tight text-white">
                    Metric Specifications
                  </h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  {p.datasheet_metric && (
                    <a
                      href={p.datasheet_metric}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="datasheet-metric-link"
                      className="inline-flex items-center gap-2 border border-white/20 px-4 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-white transition-colors hover:border-ember hover:text-ember"
                    >
                      <Download className="h-3.5 w-3.5" /> Metric Datasheet
                    </a>
                  )}
                  {p.source_url && (
                    <a
                      href={p.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="bkt-source-link"
                      className="inline-flex items-center gap-2 border border-white/20 px-4 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-white transition-colors hover:border-ember hover:text-ember"
                    >
                      View on BKT <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
              <div className="mt-5 overflow-x-auto border border-white/10 bg-obsidian-2">
                <table className="min-w-[900px] w-full border-collapse text-left">
                  <thead className="bg-obsidian-3">
                    <tr>
                      {technicalColumns.map((key) => (
                        <th key={key} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                          {labelize(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {p.specs.map((row, index) => (
                      <tr key={`${row.size || "size"}-${row.version || "version"}-${index}`} className="align-top">
                        {technicalColumns.map((key) => (
                          <td key={key} className="whitespace-nowrap px-4 py-3 text-sm text-slate-300">
                            {row[key] || "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(p.related || []).length > 0 && (
            <div className="mt-20">
              <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">Related Products</p>
              </Reveal>
              <div className="mt-8 grid gap-6 sm:grid-cols-3">
                {p.related.map((r) => (
                  <Reveal key={r.id}>
                    <Link
                      to={`/products/${r.id}`}
                      data-testid={`related-product-${r.id}`}
                      className="group block border border-white/10 bg-obsidian-2 transition-colors hover:border-white/25"
                    >
                      <div className="h-36 overflow-hidden bg-obsidian-3">
                        <img
                          src={r.image || IMAGES.tread}
                          alt={`${r.brand} ${r.name} ${r.size} tyre`}
                          className="h-full w-full object-contain p-4 img-industrial transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-5">
                        <p className="font-mono text-[10px] tracking-[0.22em] text-ember">{r.size}</p>
                        <p className="mt-1 font-condensed text-xl font-bold uppercase tracking-wide text-white">{r.name}</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
